import React, { useState } from "react";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import ImagePickerDialog from "../ImagePickerDialog";
import AudioPickerDialog from "../AudioPickerDialog";

const OPTION_IDS = ["А", "Б", "В", "Г"];

const normalizeOptions = (options) =>
    Array.isArray(options)
        ? options.map((option, idx) => ({
            id: String(option?.id || OPTION_IDS[idx] || idx + 1).trim(),
            text: String(option?.text || ""),
            imgPath: String(option?.imgPath || ""),
        }))
        : [];

const QuestionFormImageQuiz = ({ value, onChange }) => {
    const v = value || {};
    const options = normalizeOptions(v.options);
    const [pickerIndex, setPickerIndex] = useState(null);
    const [audioPickerOpen, setAudioPickerOpen] = useState(false);

    function emitOptions(nextOptions) {
        onChange({ ...v, options: nextOptions });
    }

    function updateOption(idx, patch) {
        emitOptions(options.map((option, i) => (i === idx ? { ...option, ...patch } : option)));
    }

    function addOption() {
        if (options.length >= 4) return;
        const nextId = OPTION_IDS[options.length] || String(options.length + 1);
        emitOptions([...options, { id: nextId, text: "", imgPath: "" }]);
    }

    function removeOption(idx) {
        if (options.length <= 2) return;
        const removedId = options[idx]?.id;
        const nextOptions = options.filter((_, i) => i !== idx);
        const nextCorrectAnswer = v.correctAnswer === removedId ? nextOptions[0]?.id || "" : v.correctAnswer;

        onChange({ ...v, options: nextOptions, correctAnswer: nextCorrectAnswer });
    }

    function pickImage(img) {
        if (pickerIndex == null) return;
        updateOption(pickerIndex, { imgPath: img.url, text: options[pickerIndex]?.text || img.originalFilename || "" });
        setPickerIndex(null);
    }

    function pickAudio(audio) {
        onChange({ ...v, audioPath: audio.url });
        setAudioPickerOpen(false);
    }

    return (
        <Stack spacing={2}>
            <TextField
                label="Підзаголовок"
                value={v.subtitle || ""}
                onChange={(e) => onChange({ ...v, subtitle: e.target.value })}
                helperText="Необов'язково. Показується під назвою модуля."
                fullWidth
            />

            <TextField
                label="Питання / title (HTML)"
                value={v.question || ""}
                onChange={(e) => onChange({ ...v, question: e.target.value })}
                helperText="Необов'язково, якщо завдання озвучується аудіо."
                fullWidth
                multiline
                minRows={3}
            />

            <TextField
                label="Аудіо URL"
                value={v.audioPath || ""}
                onChange={(e) => onChange({ ...v, audioPath: e.target.value })}
                helperText="Можна вставити URL вручну або обрати файл з аудіо-бібліотеки."
                fullWidth
            />

            <Button variant="outlined" onClick={() => setAudioPickerOpen(true)}>
                Обрати / завантажити аудіо
            </Button>

            {v.audioPath && (
                <Box>
                    <audio controls src={v.audioPath} style={{ width: "100%", maxWidth: 520 }}>
                        Your browser does not support the audio element.
                    </audio>
                </Box>
            )}

            <FormControl fullWidth>
                <InputLabel id="image-quiz-correct-answer-label">Правильна відповідь</InputLabel>
                <Select
                    labelId="image-quiz-correct-answer-label"
                    label="Правильна відповідь"
                    value={v.correctAnswer || ""}
                    onChange={(e) => onChange({ ...v, correctAnswer: e.target.value })}
                >
                    {options.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                            {option.id}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Варіанти зображень
            </Typography>

            {options.map((option, idx) => (
                <Box
                    key={`${option.id}-${idx}`}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2,
                    }}
                >
                    <Stack spacing={1.5}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <TextField
                                label="ID"
                                value={option.id}
                                onChange={(e) => updateOption(idx, { id: e.target.value })}
                                sx={{ width: { sm: 120 } }}
                            />
                            <TextField
                                label="Підпис / alt"
                                value={option.text}
                                onChange={(e) => updateOption(idx, { text: e.target.value })}
                                fullWidth
                            />
                        </Stack>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                            <TextField
                                label="URL зображення"
                                value={option.imgPath}
                                onChange={(e) => updateOption(idx, { imgPath: e.target.value })}
                                fullWidth
                            />
                            <Button variant="outlined" onClick={() => setPickerIndex(idx)}>
                                Обрати
                            </Button>
                            <Button
                                color="error"
                                onClick={() => removeOption(idx)}
                                disabled={options.length <= 2}
                            >
                                Видалити
                            </Button>
                        </Stack>

                        {option.imgPath && (
                            <Box
                                component="img"
                                src={option.imgPath}
                                alt={option.text || option.id}
                                sx={{
                                    width: "100%",
                                    maxWidth: 220,
                                    aspectRatio: "4 / 3",
                                    objectFit: "contain",
                                    bgcolor: "grey.100",
                                    borderRadius: 1,
                                }}
                            />
                        )}
                    </Stack>
                </Box>
            ))}

            <Button variant="outlined" onClick={addOption} disabled={options.length >= 4}>
                Додати варіант
            </Button>

            <ImagePickerDialog
                open={pickerIndex != null}
                onClose={() => setPickerIndex(null)}
                onPick={pickImage}
            />

            <AudioPickerDialog
                open={audioPickerOpen}
                onClose={() => setAudioPickerOpen(false)}
                onPick={pickAudio}
            />
        </Stack>
    );
};

export default QuestionFormImageQuiz;
